import { useCallback, useMemo, useRef, useState } from "react";
import { Table, Slider } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import TrancheSummaryTable from "./TrancheSummaryTable";
import useFormatTrancheSummaryResults from "../../hooks/useFormatTrancheSummaryResults";
import dayjs from "dayjs";
import {
  emdash,
  RadioSelector,
  toDollar,
} from "../../utils/Constants/systemSetting";

export default function CombinedSchedule({
  companyInfo,
  calculatorPages,
  allTrancheTableValues,
  optionsCalculatorValues,
  RSUCalculatorValues,
  generalInfoParams,
  setGeneralInfoParams,
  setUpdateNeeded,
  includeTaxes = false,
  currentStockPrice = 0,
}) {
  const combinedOptionSchedule = useFormatTrancheSummaryResults(
    allTrancheTableValues,
    "Options Schedule",
    optionsCalculatorValues,
  );
  const combinedRSUSchedule = useFormatTrancheSummaryResults(
    allTrancheTableValues,
    "RSU Schedule",
    RSUCalculatorValues,
  );
  const visualizationTypeOptions = ["Units", "Dollars"];
  const [visualizationType, setVisualizationType] = useState("Dollars");

  const taxOptions = ["Pre-Tax", "After Tax", "Both"];
  const [taxOptionSelected, setTaxOptionSelected] = useState("Pre-Tax");

  const allCombinedSchedules = useMemo(() => {
    return [
      {
        scheduleType: "Options Schedule",
        scheduleData: combinedOptionSchedule,
      },
      { scheduleType: "RSU Schedule", scheduleData: combinedRSUSchedule },
    ];
  }, [combinedOptionSchedule, combinedRSUSchedule]);

  const formattedCombinedScheduleData = useMemo(() => {
    const combinedScheduleData = {};
    if (!allCombinedSchedules) return [];

    allCombinedSchedules.forEach((combinedSchedule) => {
      // TODO: Use other variables from combinedSchedule to get $ amount graph
      // And way to visualize kill percent/amount in the graph
      // const combinedScheduleSampleData = {
      //   ID: "Tranche 0",
      //   key: 0,
      //   trancheDate: "2023-09-21T04:00:00.000Z",
      //   initialUnits: 31587,
      //   remainingUnits: 31587,
      //   remainingFullValue: 1485046.0532642344,
      //   intrinsicValue: 710707.5,
      //   killAmount: 774338.5532642344,
      //   killPercentage: 52.142393265325644,
      // };

      const scheduleType = combinedSchedule.scheduleType;
      const scheduleData = combinedSchedule.scheduleData;

      const getTrancheValue = (tranche, dataKey) =>
        !isNaN(parseFloat(tranche?.[dataKey]))
          ? parseFloat(tranche?.[dataKey])
          : 0;

      scheduleData.forEach((tranche) => {
        const dateExceeded = dayjs(tranche.trancheDate).isAfter(dayjs());
        const remainingUnits =
          visualizationType === "Units"
            ? getTrancheValue(tranche, "remainingUnits")
            : taxOptionSelected === "Pre-Tax"
              ? getTrancheValue(tranche, "remainingFullValue")
              : getTrancheValue(tranche, "remainingFullValueAfterTax");
        const taxAmount =
          getTrancheValue(tranche, "remainingFullValue") -
          getTrancheValue(tranche, "remainingFullValueAfterTax");

        //anticipated amounts
        const remainingUnitsAnticipated =
          visualizationType === "Units"
            ? getTrancheValue(tranche, "anticipated-remainingUnits")
            : taxOptionSelected === "Pre-Tax"
              ? getTrancheValue(tranche, "anticipated-remainingFullValue")
              : getTrancheValue(
                  tranche,
                  "anticipated-remainingFullValueAfterTax",
                );
        const taxAmountAnticipated =
          getTrancheValue(tranche, "anticipated-remainingFullValue") -
          getTrancheValue(tranche, "anticipated-remainingFullValueAfterTax");

        const killAmount =
          taxOptionSelected === "Pre-Tax"
            ? getTrancheValue(tranche, "killAmount")
            : getTrancheValue(tranche, "killAmountAfterTax");
        const killAmountTaxed =
          getTrancheValue(tranche, "killAmount") -
          getTrancheValue(tranche, "killAmountAfterTax");

        if (combinedScheduleData?.[tranche.trancheDate]) {
          const currentTrancheData = combinedScheduleData[tranche.trancheDate];

          combinedScheduleData[tranche.trancheDate] = {
            ...combinedScheduleData[tranche.trancheDate],
            taxAmount:
              currentTrancheData["taxAmount"] +
              taxAmount +
              (taxAmountAnticipated || 0),
            killAmount: currentTrancheData["killAmount"] + killAmount,
            killAmountTaxed: currentTrancheData["killAmountTaxed"] + killAmount,
            options:
              scheduleType === "Options Schedule" && !dateExceeded
                ? currentTrancheData["options"] + remainingUnits
                : currentTrancheData["options"],
            RSUs:
              scheduleType === "RSU Schedule" && !dateExceeded
                ? currentTrancheData["RSUs"] + remainingUnits
                : currentTrancheData["RSUs"],
            unvestedOptions:
              scheduleType === "Options Schedule" && dateExceeded
                ? currentTrancheData["unvestedOptions"] + remainingUnits
                : currentTrancheData["unvestedOptions"],
            unvestedRSUs:
              scheduleType === "RSU Schedule" && dateExceeded
                ? currentTrancheData["unvestedRSUs"] + remainingUnits
                : currentTrancheData["unvestedRSUs"],
            anticipatedRSUs:
              scheduleType === "RSU Schedule"
                ? currentTrancheData["anticipatedRSUs"] +
                  remainingUnitsAnticipated
                : currentTrancheData["anticipatedRSUs"],
            anticipatedOptions:
              scheduleType === "Options Schedule"
                ? currentTrancheData["anticipatedOptions"] +
                  remainingUnitsAnticipated
                : currentTrancheData["anticipatedOptions"],
          };
        } else {
          combinedScheduleData[tranche.trancheDate] = {
            ...tranche,
            date: dayjs(tranche.trancheDate).format("M/D/YY"),
            taxAmount: taxAmount + (taxAmountAnticipated || 0),
            killAmount: killAmount,
            killAmountTaxed: killAmountTaxed,
            options:
              scheduleType === "Options Schedule" && !dateExceeded
                ? remainingUnits
                : 0,
            RSUs:
              scheduleType === "RSU Schedule" && !dateExceeded
                ? remainingUnits
                : 0,
            unvestedOptions:
              scheduleType === "Options Schedule" && dateExceeded
                ? remainingUnits
                : 0,
            unvestedRSUs:
              scheduleType === "RSU Schedule" && dateExceeded
                ? remainingUnits
                : 0,
            anticipatedRSUs:
              scheduleType === "RSU Schedule" ? remainingUnitsAnticipated : 0,
            anticipatedOptions:
              scheduleType === "Options Schedule"
                ? remainingUnitsAnticipated
                : 0,
          };
        }
      });
    });
    return Object.values(combinedScheduleData)
      .sort((a, b) => new Date(a.trancheDate) - new Date(b.trancheDate))
      .map((item, index) => ({
        ...item,
        key: index,
        ID: `Tranche ${index + 1}`,
      }));
  }, [allCombinedSchedules, visualizationType, taxOptionSelected]);

  const columns = useMemo(() => {
    const renderItem = (item) =>
      item
        ? visualizationType === "Units"
          ? item.toLocaleString()
          : toDollar(item)
        : emdash;
    const defaultColumns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        align: "center",
      },
      {
        title: "Options",
        dataIndex: "options",
        key: "options",
        align: "center",
        render: (item) => renderItem(item),
      },
      {
        title: "Unvested Options",
        dataIndex: "unvestedOptions",
        key: "unvestedOptions",
        align: "center",
        render: (item) => renderItem(item),
      },
      {
        title: "Anticipated Options",
        dataIndex: "anticipatedOptions",
        key: "anticipatedOptions",
        align: "center",
        render: (item) => renderItem(item),
      },
      {
        title: "RSUs",
        dataIndex: "RSUs",
        key: "RSUs",
        align: "center",
        render: (item) => renderItem(item),
      },
      {
        title: "Unvested RSUs",
        dataIndex: "unvestedRSUs",
        key: "unvestedRSUs",
        align: "center",
        render: (item) => renderItem(item),
      },
      {
        title: "Anticipated RSUs",
        dataIndex: "anticipatedRSUs",
        key: "anticipatedRSUs",
        align: "center",
        render: (item) => renderItem(item),
      },
    ];

    if (visualizationType === "Dollars") {
      if (taxOptionSelected === "Both") {
        defaultColumns.push({
          title: "Tax Amount",
          dataIndex: "taxAmount",
          key: "taxAmount",
          align: "center",
          render: (item) => renderItem(item),
        });
      }
      //This one gets added either way if "Dollars" selected
      defaultColumns.push({
        title: "Kill Amount",
        dataIndex: "killAmount",
        key: "killAmount",
        align: "center",
        render: (item) => renderItem(item),
      });

      if (taxOptionSelected === "Both") {
        defaultColumns.push({
          title: "Kill Amount Taxed",
          dataIndex: "killAmountTaxed",
          key: "killAmountTaxed",
          align: "center",
          render: (item) => renderItem(item),
        });
      }
    }

    return defaultColumns;
  }, [visualizationType, taxOptionSelected]);

  const SummaryValue = ({ title, value, isAlwaysDollar = false }) => {
    const formatValue = (value) =>
      visualizationType === "Units" && !isAlwaysDollar
        ? `${value.toLocaleString()} units`
        : toDollar(value);
    return (
      <div>
        <span style={{ fontWeight: "bold" }}>{title}: </span>
        <span>{formatValue(value)}</span>
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((s, p) => s + p.value, 0);
    //p.fill color to match color of bar
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #3a3a5c",
          // borderRadius: 8,
          padding: "10px 14px",
        }}
      >
        <p style={{ color: "", fontWeight: 600, marginBottom: 6 }}>{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: "black", margin: "2px 0" }}>
            {p.name}:{" "}
            <strong style={{ color: p.fill }}>
              {visualizationType === "Units"
                ? `${p.value.toLocaleString()} Awards`
                : `$${p.value.toLocaleString()}`}
            </strong>
          </p>
        ))}
        <p
          style={
            {
              // color: "",
              // borderTop: "1px solid #3a3a5c",
              // marginTop: 6,
              // paddingTop: 6,
            }
          }
        >
          Total:{" "}
          <strong style={{ color: "" }}>
            {visualizationType === "Units"
              ? `${total.toLocaleString()} Awards`
              : `$${total.toLocaleString()}`}
          </strong>
        </p>
      </div>
    );
  };

  const getPackageTotal = (payPackage, keyNeeded, isAlwaysDollar = false) => {
    let total = 0;
    const allKeys = ["RSUs", "unvestedRSUs", "options", "unvestedOptions"];
    const keysNeeded = keyNeeded === "total" ? allKeys : [keyNeeded];

    keysNeeded.forEach((key) => {
      payPackage.forEach((tranche) => {
        total += tranche?.[key] || 0;
      });
    });

    return total;
  };

  const marks = {
    0: "$0",
    // 55: "$55", //Typical default value for testing
    [currentStockPrice]: (
      <div>
        <p>{toDollar(currentStockPrice)}</p>
        <p style={{ fontSize: 10 }}>
          {companyInfo?.Ticker}&nbsp;Share&nbsp;Price
        </p>
      </div>
    ),
    250: "$250",
  };

  const adjustSharePrice = useCallback(
    (val) => {
      setGeneralInfoParams((prev) => {
        const newGeneralInfoParams = [...prev];

        newGeneralInfoParams[0] = {
          ...newGeneralInfoParams[0],
          sharePrice: parseFloat(val),
        };
        return [...newGeneralInfoParams];
      });
      setUpdateNeeded(true);
    },
    [setGeneralInfoParams, setUpdateNeeded],
  );

  return (
    <div>
      <svg style={{ position: "absolute", width: 0, height: 0 }}>
        <defs>
          <pattern
            id="hatch"
            patternUnits="userSpaceOnUse"
            width={6}
            height={6}
          >
            <path
              d="M0,6 l6,-6 M-1,1 l2,-2 M5,7 l2,-2"
              stroke="var(--secondary-2)"
              strokeWidth={1.5}
            />
          </pattern>
        </defs>
      </svg>
      <svg style={{ position: "absolute", width: 0, height: 0 }}>
        <defs>
          <pattern
            id="crosshatch"
            patternUnits="userSpaceOnUse"
            width={6}
            height={6}
          >
            <path
              d="M0,6 l6,-6 M-1,1 l2,-2 M5,7 l2,-2"
              stroke="var(--primary-1)"
              strokeWidth={1.5}
            />
            <path
              d="M6,0 l-6,6 M7,5 l-2,2 M1,-1 l-2,2"
              stroke="var(--primary-1)"
              strokeWidth={1.5}
            />
          </pattern>
        </defs>
      </svg>
      <RadioSelector
        options={visualizationTypeOptions}
        state={visualizationType}
        setState={setVisualizationType}
        heading="Display:"
        style={{ justifyContent: "center" }}
      />
      {visualizationType === "Dollars" && (
        <RadioSelector
          options={taxOptions}
          state={taxOptionSelected}
          setState={setTaxOptionSelected}
          heading=""
          style={{ justifyContent: "center" }}
        />
      )}

      <div
        style={{
          // minHeight: "80vh",
          display: "flex",
          // marginTop: "-75px",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          // padding: 32,
        }}
      >
        {/* <h2
          style={{
            marginBottom: 24,
            fontSize: 20,
            letterSpacing: 0.5,
          }}
        >
          Equity Awards by Vest Date
        </h2> */}
        <ResponsiveContainer width="100%" height={750}>
          <BarChart
            layout="vertical"
            data={formattedCombinedScheduleData}
            margin={{ top: 10, right: 40, left: 20, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#2a2a3e"
              horizontal={false}
            />
            <XAxis
              type="number"
              tick={{ fill: "", fontSize: 12 }}
              axisLine={{ stroke: "#3a3a5c" }}
              tickLine={false}
              tickFormatter={(val) =>
                visualizationType === "Units"
                  ? val.toLocaleString()
                  : toDollar(val, 0, true)
              }
              label={{
                value:
                  visualizationType === "Units" ? "# of Awards" : "Dollars",
                position: "insideBottom",
                offset: -2,
                fill: "#7f849c",
                fontSize: 13,
              }}
            />
            <YAxis
              type="category"
              dataKey="date"
              tick={{ fill: "", fontSize: 12 }}
              axisLine={{ stroke: "#3a3a5c" }}
              tickLine={false}
              width={72}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(255,255,255,0.04)" }}
            />
            <Legend
              wrapperStyle={{ color: "", fontSize: 13, paddingTop: 16 }}
              formatter={(val) => <span style={{ color: "black" }}>{val}</span>}
            />
            <Bar
              dataKey="options"
              name="Options"
              stackId="a"
              fill="var(--primary-1)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="unvestedOptions"
              name="Unvested Options"
              stackId="a"
              fill="var(--primary-2)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="anticipatedOptions"
              name="Anticipated Options"
              stackId="a"
              fill="url(#crosshatch)"
              stroke="var(--primary-1)"
              strokeWidth={1}
              radius={[0, 0, 0, 0]}
            />

            <Bar
              dataKey="RSUs"
              name="RSUs"
              stackId="a"
              fill="var(--secondary-1)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="unvestedRSUs"
              name="Unvested RSUs"
              stackId="a"
              fill="var(--secondary-2)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="anticipatedRSUs"
              name="Anticipated RSUs"
              stackId="a"
              fill="url(#hatch)"
              stroke="var(--secondary-2)"
              strokeWidth={1}
              radius={[0, 0, 0, 0]}
            />

            {visualizationType === "Dollars" &&
              taxOptionSelected === "Both" && (
                <Bar
                  dataKey="taxAmount"
                  name="Tax Amount"
                  stackId="a"
                  fill="var(--dark-red)"
                  radius={[0, 0, 0, 0]}
                />
              )}

            {visualizationType === "Dollars" &&
              taxOptionSelected === "Both" && (
                <Bar
                  dataKey="killAmountTaxed"
                  name="Kill Amount Taxed"
                  stackId="a"
                  fill="var(--gray-red)"
                  radius={[0, 0, 0, 0]}
                />
              )}

            {visualizationType === "Dollars" && (
              <Bar
                dataKey="killAmount"
                name="Kill Amount"
                stackId="a"
                fill="var(--gray-1)"
                radius={[0, 0, 0, 0]}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div>
        {visualizationType !== "Units" && (
          <>
            <hr className="divider-line" />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "50%", textAlign: "center" }}>
                <h3 style={{ textAlign: "center", fontSize: "14pt" }}>
                  Adjust Share Price by Sliding the Bar
                </h3>

                <Slider
                  min={0}
                  max={250}
                  marks={marks}
                  value={generalInfoParams?.[0]?.sharePrice}
                  onChange={(val) => {
                    adjustSharePrice(val);
                  }}
                />
              </div>
            </div>
            <p style={{ textAlign: "center", fontSize: "14pt", marginTop: 10 }}>
              Current Share Price set to:{" "}
              <b>
                $
                {generalInfoParams?.[0]?.sharePrice
                  ? parseFloat(generalInfoParams?.[0]?.sharePrice).toFixed(2)
                  : ""}
              </b>
            </p>
            <hr className="divider-line" />
          </>
        )}
        <h3 style={{ textAlign: "center" }}>Package Totals</h3>
        <div style={{ textAlign: "center", fontSize: "18px" }}>
          <p style={{ fontWeight: "bold", fontSize: "24px" }}>
            In {visualizationType},{" "}
            {taxOptionSelected === "Both" ? "After Tax" : taxOptionSelected}
          </p>
          <p style={{ fontSize: "16px" }}>
            (This option can be adjusted at the top of the page)
          </p>
          <SummaryValue
            title={"Vested RSUs"}
            value={getPackageTotal(formattedCombinedScheduleData, "RSUs")}
          />
          <SummaryValue
            title={"Unvested RSUs"}
            value={getPackageTotal(
              formattedCombinedScheduleData,
              "unvestedRSUs",
            )}
          />
          <SummaryValue
            title={"Vested Options"}
            value={getPackageTotal(formattedCombinedScheduleData, "options")}
          />
          <SummaryValue
            title={"Unvested Options"}
            value={getPackageTotal(
              formattedCombinedScheduleData,
              "unvestedOptions",
            )}
          />
          <SummaryValue
            title={"Total"}
            value={getPackageTotal(formattedCombinedScheduleData, "total")}
          />
          {visualizationType === "Dollars" && (
            <div>
              {/* <h3>Summary Values</h3>
            <SummaryValue
              title={`Total Package${taxOptionSelected !== "Pre-Tax" ? " (After Tax)" : ""}`}
              value={getPackageTotal(
                [...optionsCalculatorValues, ...RSUCalculatorValues],
                taxOptionSelected === "Pre-Tax"
                  ? "remainingGrantValue"
                  : "remainingGrantValueAfterTax",
              )}
              isAlwaysDollar={true}
            /> */}
              <SummaryValue
                title={`Executive's Combined Delta`}
                value={getPackageTotal(
                  [...optionsCalculatorValues, ...RSUCalculatorValues],
                  taxOptionSelected === "Pre-Tax"
                    ? "remainingGrantDelta"
                    : "remainingGrantDeltaAfterTax",
                )}
                isAlwaysDollar={true}
              />
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 15 }}>
        <h3 style={{ textAlign: "center" }}>Combined Vesting Schedule</h3>
        <Table
          columns={columns}
          dataSource={formattedCombinedScheduleData}
          size="small"
          pagination={{
            hideOnSinglePage: true,
            pageSize: formattedCombinedScheduleData.length,
          }}
        />
        {/* {calculatorPages
          .filter((calculatorPage) => calculatorPage !== "Combined Schedule")
          .map((calculatorPage) => (
            <div style={{ marginTop: 15 }} key={calculatorPage}>
              <h3>
                {calculatorPage.replace(" Schedule", "")} Vesting Schedule
              </h3>
              <TrancheSummaryTable
                calculatorPageSelected={calculatorPage}
                allTrancheTableValues={allTrancheTableValues}
                includeTaxes={includeTaxes}
              />
            </div>
          ))} */}
      </div>
      <div style={{ margin: 50 }} />
    </div>
  );
}
